// ABOUTME: Chrome-less shell for the in-app auth screens (/auth/*) — backdrop, logo, card slot.
// ABOUTME: Desktop is the dark backdrop with the full lockup; below sm the page is white with the TE mark.

import type { FC, ReactNode } from "react"
import { AUTH_BG_CLASS } from "@shared/constants/authTheme"
// Imported, not referenced by public-root path: the design system ships these components
// outside the app, where "/logo-te-full.svg" resolves against a host that does not have it.
import logoFull from "@/assets/img/logo-te-full.svg"
import logoMark from "@/assets/img/logo-te-mark.svg"
import { CALENDAR_PAGE } from "@shared/constants/navigation"

interface AuthLayoutProps {
  children: ReactNode
}

/**
 * AuthLayout — deliberately renders NO app sidebar/navbar: auth pages live outside the app shell.
 *
 * The two modes come from the design (docs/DESIGN_SYSTEM.md §4.2): dark backdrop + 64px lockup on
 * desktop, white full-height page + 36px mark pinned left below `sm`.
 */
export const AuthLayout: FC<AuthLayoutProps> = ({ children }) => (
  // The app disables body scroll globally (index.css), so the layout is its own
  // scrollport; the min-h-full wrapper keeps the card centred without clipping the
  // top when the content is taller than the viewport.
  <div className={`h-dvh overflow-y-auto ${AUTH_BG_CLASS} max-sm:bg-white`}>
    <div className="flex min-h-full items-center justify-center px-5 py-12 max-sm:items-start max-sm:pt-6 max-sm:pb-12">
      <div className="flex w-full max-w-[440px] flex-col items-center gap-7 max-sm:items-stretch max-sm:gap-4">
        <a
          href={CALENDAR_PAGE}
          className="leading-none max-sm:self-start"
        >
          {/* The browser picks the artwork: the round mark on phones, the lockup everywhere else. */}
          <picture>
            <source
              media="(max-width: 639px)"
              srcSet={logoMark}
            />
            <img
              src={logoFull}
              alt="Training Endurance"
              className="block h-16 w-auto max-sm:h-9"
            />
          </picture>
        </a>
        {children}
      </div>
    </div>
  </div>
)
